import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ScrollTransition.css';

const ScrollTransition = () => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionDirection, setTransitionDirection] = useState('down'); // 'down' or 'up'
  const [sparkles, setSparkles] = useState([]);
  const lastScrollY = useRef(0);
  const isTransitioningRef = useRef(false);
  const sparkleIntervalRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const scrollDirection = scrollY > lastScrollY.current ? 'down' : 'up';
      lastScrollY.current = scrollY;

      // Trigger transition when crossing threshold in either direction
      const wallThreshold = window.innerHeight * 0.5;

      // Find the desk section element to get its exact position
      const deskSection = document.querySelector('.desk-section');
      const deskTop = deskSection ? deskSection.offsetTop : window.innerHeight * 1.5;

      // Don't trigger if already transitioning
      if (isTransitioningRef.current) return;

      // Scrolling down past wall - scroll exactly to desk top (not past it)
      if (scrollDirection === 'down' && scrollY > 50 && scrollY < wallThreshold) {
        triggerTransition('down', deskTop);
      }
      // Scrolling up past desk
      else if (scrollDirection === 'up' && scrollY < deskTop && scrollY > wallThreshold) {
        triggerTransition('up', 0);
      }
    };

    const triggerTransition = (direction, targetScroll) => {
      console.log('🌟 TRIGGERING TRANSITION! direction:', direction, 'targetScroll:', targetScroll);
      isTransitioningRef.current = true;
      setIsTransitioning(true);
      setTransitionDirection(direction);

      // Start creating sparkles
      createSparkles();

      // Block user scroll input during transition
      const preventScroll = (e) => {
        e.preventDefault();
        e.stopPropagation();
      };
      window.addEventListener('wheel', preventScroll, { passive: false });
      window.addEventListener('touchmove', preventScroll, { passive: false });
      document.addEventListener('keydown', (e) => {
        if (['ArrowUp', 'ArrowDown', 'Space', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
          e.preventDefault();
        }
      });

      // Smooth scroll to target
      window.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });

      // Stop sparkles and transition after animation completes
      setTimeout(() => {
        // Re-enable scroll input
        window.removeEventListener('wheel', preventScroll);
        window.removeEventListener('touchmove', preventScroll);
        setIsTransitioning(false);
        isTransitioningRef.current = false;
        if (sparkleIntervalRef.current) {
          clearInterval(sparkleIntervalRef.current);
        }
        // Clear sparkles after a delay
        setTimeout(() => setSparkles([]), 500);
      }, 1500); // Duration of scroll animation
    };

    const createSparkles = () => {
      console.log('✨ Creating sparkles...');
      let sparkleCount = 0;
      sparkleIntervalRef.current = setInterval(() => {
        if (sparkleCount < 60) { // Create 60 sparkles total (doubled from 30)
          // Create 2 sparkles per interval for faster generation
          for (let i = 0; i < 2; i++) {
            const newSparkle = {
              id: Date.now() + Math.random(),
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              size: Math.random() * 25 + 15, // Larger sparkles (15-40px vs 10-30px)
              delay: Math.random() * 0.1,
            };
            setSparkles(prev => [...prev, newSparkle]);
            sparkleCount++;
          }
        }
      }, 30); // Faster interval (30ms vs 50ms)
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (sparkleIntervalRef.current) {
        clearInterval(sparkleIntervalRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* Motion blur overlay during transition */}
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            className="motion-blur-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>

      {/* Sparkle particles */}
      <div className="sparkle-container">
        <AnimatePresence>
          {sparkles.map((sparkle) => (
            <motion.div
              key={sparkle.id}
              className="sparkle"
              initial={{
                x: sparkle.x,
                y: sparkle.y,
                scale: 0,
                opacity: 0,
              }}
              animate={{
                scale: [0, 1.8, 0],
                opacity: [0, 1, 0],
                rotate: [0, 180, 360],
                y: sparkle.y + (transitionDirection === 'down' ? 100 : -100), // Move based on direction
              }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{
                duration: 1.2,
                delay: sparkle.delay,
                ease: 'easeOut',
              }}
              style={{
                width: sparkle.size,
                height: sparkle.size,
              }}
            />
          ))}
        </AnimatePresence>
      </div>
    </>
  );
};

export default ScrollTransition;
